// All HTTP calls to /api. Schema mirrors the build-decoupled redesign spec
// (docs/superpowers/specs/2026-05-18-build-decoupled-redesign.md §14).

import { request } from './lib/http'

const req = request

// -------- auth --------
export interface User { id: number; username: string }
export const login = (username: string, password: string) =>
  req<User>('/api/login', { method: 'POST', body: JSON.stringify({ username, password }) })
export const logout = async () => { await fetch('/api/logout', { method: 'POST', credentials: 'include' }) }
export const me = async (): Promise<User | null> => {
  const res = await fetch('/api/me', { credentials: 'include' })
  if (res.status === 401) return null
  if (!res.ok) throw new Error(await res.text())
  return res.json()
}

// -------- services --------
export interface Service {
  id: number
  name: string
  desc?: string
  image_repo: string
  domain?: string
  container_port: number
  env_json: string
  volumes_json: string
  health_path?: string
  health_initial_delay_sec: number
  health_timeout_sec: number
  pre_deploy_cmd?: string
  post_deploy_cmd?: string
  hook_timeout_sec: number
  auto_deploy_on_push: boolean
  auto_rollback: boolean
  current_version_id?: number | null
  created_at: number
}

export type ServiceInput = Partial<Service> & {
  name: string
  image_repo: string
  container_port?: number
}

export type ServiceOperationKind = 'idle' | 'deploy' | 'rollback' | 'start' | 'stop' | 'restart'
export type ServiceOperationStatus = 'idle' | 'running' | 'success' | 'failed' | 'cancelled'

export interface ServiceOperation {
  kind: ServiceOperationKind
  status: ServiceOperationStatus
  phase?: string
  deployment_id?: number
  error?: string
  started_at?: number
  ended_at?: number
}

export interface ServiceOverviewHealth {
  state: 'up' | 'down' | 'unknown'
  container_state: 'running' | 'stopped' | 'missing' | 'unknown'
  last_check_at?: number
  last_change_at?: number
  consecutive_failures?: number
  last_error?: string
}

export interface ServiceOverview {
  service: Service
  health: ServiceOverviewHealth
  operation: ServiceOperation
}

export const services = {
  list: () => req<Service[]>('/api/services/'),
  get: (id: number) => req<Service>(`/api/services/${id}`),
  overviewList: () => req<ServiceOverview[]>('/api/services/overview'),
  overviewGet: (id: number) => req<ServiceOverview>(`/api/services/${id}/overview`),
  create: (s: ServiceInput) =>
    req<Service>('/api/services/', { method: 'POST', body: JSON.stringify(s) }),
  update: (id: number, s: ServiceInput) =>
    req<void>(`/api/services/${id}`, { method: 'PATCH', body: JSON.stringify(s) }),
  delete: (id: number) =>
    req<void>(`/api/services/${id}`, { method: 'DELETE' }),
  start: (id: number) => req<LifecycleOp>(`/api/services/${id}/start`, { method: 'POST' }),
  stop: (id: number) => req<LifecycleOp>(`/api/services/${id}/stop`, { method: 'POST' }),
  restart: (id: number) => req<LifecycleOp>(`/api/services/${id}/restart`, { method: 'POST' }),
}

// -------- versions --------
export interface Version {
  id: number
  service_id: number
  tag: string
  digest: string
  commit_sha?: string
  commit_msg?: string
  received_at: number
  size_bytes?: number
  pulled?: boolean
}
export const versions = {
  list: (serviceId: number) =>
    req<Version[]>(`/api/services/${serviceId}/versions`),
  delete: (id: number) => req<void>(`/api/versions/${id}`, { method: 'DELETE' }),
}

// -------- deployments --------
export interface Deployment {
  id: number
  service_id: number
  version_id: number
  status: string
  started_at: number
  finished_at?: number
  error?: string
  log_blob?: string
  triggered_by: string
}
export const deployments = {
  list: (serviceId: number) => req<Deployment[]>(`/api/services/${serviceId}/deployments/`),
  get: (id: number) => req<Deployment>(`/api/deployments/${id}`),
  deploy: (serviceId: number, versionId: number, note = '') =>
    req<Deployment>(`/api/services/${serviceId}/deployments/deploy`, {
      method: 'POST', body: JSON.stringify({ version_id: versionId, note }),
    }),
  rollback: (serviceId: number) =>
    req<Deployment>(`/api/services/${serviceId}/rollback`, { method: 'POST' }),
  cancel: (id: number) =>
    req<{ ok: boolean }>(`/api/deployments/${id}/cancel`, { method: 'POST' }),
  logURL: (id: number) => `/api/deployments/${id}/log`,
}

// -------- tokens --------
export interface Token { id: number; name: string; created_at: number; last_used_at?: number }
export interface CreatedToken extends Token { token: string }
export const tokens = {
  list: () => req<Token[]>('/api/tokens/'),
  create: (name: string) =>
    req<CreatedToken>('/api/tokens/', { method: 'POST', body: JSON.stringify({ name }) }),
  delete: (id: number) => req<void>(`/api/tokens/${id}`, { method: 'DELETE' }),
}

// -------- notifiers --------
export interface Notifier {
  id: number
  name: string
  url: string
  events: string[]
  template?: string
  headers?: Record<string, string>
  enabled: boolean
  allow_private: boolean
  created_at: number
}
export type NotifierInput = Omit<Notifier, 'id' | 'created_at'>
export const notifiers = {
  list: () => req<Notifier[]>('/api/notifiers/'),
  create: (n: NotifierInput) =>
    req<{ id: number }>('/api/notifiers/', { method: 'POST', body: JSON.stringify(n) }),
  update: (id: number, n: NotifierInput) =>
    req<void>(`/api/notifiers/${id}`, { method: 'PATCH', body: JSON.stringify(n) }),
  delete: (id: number) => req<void>(`/api/notifiers/${id}`, { method: 'DELETE' }),
  test: (id: number) =>
    req<{ ok: boolean }>(`/api/notifiers/${id}/test`, { method: 'POST' }),
}

// -------- github integration --------
export interface GitHubConfig {
  default_owner: string
  default_repo?: string
  has_pat: boolean
  last_test_at?: number
  last_test_ok?: boolean
  last_test_error?: string
}

export interface GitHubConfigInput {
  default_repo?: string
  pat?: string
}

export interface GitHubConfigTestPayload {
  pat?: string
}

export interface GitHubConfigTestResult {
  ok: boolean
  repo?: { private: boolean }
  user?: { login: string }
  config?: GitHubConfig
}

export interface ApplyGitHubServiceSecretsInput {
  repo_slug: string
  apply_secret: boolean
  commit_workflow: boolean
  workflow_branch?: string
  workflow_path?: string
  workflow_yaml?: string
}

export interface ApplyGitHubServiceSecretsResult {
  ok: boolean
  repo_slug?: string
  secret_name?: string
  secret_applied?: boolean
  workflow_committed?: boolean
  workflow_path?: string
  workflow_branch?: string
}

export interface GitHubOnboardingInfo {
  default_branch: string
  branches: string[]
}

export type RepositoryTemplateID = 'static-spa-nginx' | 'go-http' | 'existing-dockerfile'

export interface RepositoryOnboardingService {
  name: string
  image_repo: string
  domain: string
  container_port: number
  health_path: string
  auto_deploy_on_push: boolean
  auto_rollback: boolean
  build_package?: string
}

export interface RepositoryOnboardingPreviewInput {
  repo_slug: string
  branch: string
  template_id: RepositoryTemplateID
  service: RepositoryOnboardingService
  add_healthz?: boolean
  apply_secret?: boolean
}

export interface RepositoryOnboardingDetection {
  template_id: RepositoryTemplateID
  project_kind: string
  evidence: string[]
  warnings: string[]
  defaults: RepositoryOnboardingService
  has_dockerfile: boolean
  has_workflow: boolean
}

export interface RepositoryOnboardingFile {
  path: string
  content: string
  exists?: boolean
}

export interface RepositoryOnboardingPreview {
  template_id: RepositoryTemplateID
  files: RepositoryOnboardingFile[]
  warnings: string[]
  blocking_errors: string[]
  preview_hash: string
}

export interface RepositoryOnboardingApplyResult {
  ok: boolean
  repo_slug: string
  branch: string
  files_committed: number
  secret_applied: boolean
  preview: RepositoryOnboardingPreview
}

export const githubAPI = {
  getConfig: () => req<GitHubConfig>('/api/github/config'),
  saveConfig: (input: GitHubConfigInput) => {
    const payload: GitHubConfigInput = { ...input }
    if (!payload.pat?.trim()) {
      delete payload.pat
    }
    return req<GitHubConfig>('/api/github/config', { method: 'PUT', body: JSON.stringify(payload) })
  },
  testConfig: (payload: GitHubConfigTestPayload) =>
    req<GitHubConfigTestResult>('/api/github/test', { method: 'POST', body: JSON.stringify(payload) }),
  getOnboardingInfo: (repoSlug: string) =>
    req<GitHubOnboardingInfo>(`/api/github/onboarding-info?repo_slug=${encodeURIComponent(repoSlug)}`),
  detectRepositoryOnboarding: (repoSlug: string, branch: string, serviceName: string) => {
    const params = new URLSearchParams({
      repo_slug: repoSlug,
      branch,
      service_name: serviceName,
    })
    return req<RepositoryOnboardingDetection>(`/api/github/onboarding-detect?${params.toString()}`)
  },
  previewRepositoryOnboarding: (payload: RepositoryOnboardingPreviewInput) =>
    req<RepositoryOnboardingPreview>('/api/github/onboarding-preview', {
      method: 'POST', body: JSON.stringify(payload),
    }),
  applyRepositoryOnboarding: (payload: RepositoryOnboardingPreviewInput) =>
    req<RepositoryOnboardingApplyResult>('/api/github/onboarding-apply', {
      method: 'POST', body: JSON.stringify(payload),
    }),
  applyServiceSecrets: (serviceId: number, payload: ApplyGitHubServiceSecretsInput) =>
    req<ApplyGitHubServiceSecretsResult>(`/api/services/${serviceId}/github-secrets/apply`, {
      method: 'POST', body: JSON.stringify(payload),
    }),
}

// -------- health --------
export interface Health {
  service_id: number
  state: 'up' | 'down' | 'unknown'
  container_state: 'running' | 'stopped' | 'missing' | 'unknown'
  last_check_at?: number
  last_change_at?: number
  consecutive_failures?: number
  last_error?: string
}
export const healthAPI = {
  get: (id: number) => req<Health>(`/api/services/${id}/health`),
}

// -------- lifecycle --------
export type LifecycleKind = 'start' | 'stop' | 'restart' | 'deploy'
export type LifecycleStatus = 'running' | 'done' | 'failed' | 'cancelled'

export interface LifecycleOp {
  service_id: number
  kind: LifecycleKind
  phase: string
  status: LifecycleStatus
  started_at: number
  ended_at?: number
  error?: string
  deployment_id?: number
}

export const lifecycleAPI = {
  // Returns the active op or the most-recent terminal op (within ~30s).
  // null on 204 idle.
  get: async (id: number): Promise<LifecycleOp | null> => {
    const res = await fetch(`/api/services/${id}/lifecycle`, { credentials: 'include' })
    if (res.status === 204) return null
    if (!res.ok) throw new Error(await res.text())
    return res.json()
  },
}

// -------- system --------
export interface SelfUpdateTag { tag: string; created?: string; created_err?: string }
export interface SelfUpdatePhase {
  phase: string
  status: 'start' | 'ok' | 'fail' | 'skip'
  started_at: string
  ended_at?: string
  duration_ms?: number
  log: string[]
}
export interface SelfUpdateRunStatus {
  run_id: string
  target_image?: string
  initiator?: string
  started_at: string
  ended_at?: string
  terminal?: '' | 'done' | 'rollback' | 'fail'
  phases: SelfUpdatePhase[]
  note?: string
  none?: boolean
}
export interface ContainerInfo {
  container_name: string
  image_ref: string
  image_digest: string
  image_created_at: string
  container_started_at: string
}
export interface VersionResp {
  go_version: string
  container?: ContainerInfo
}
export const system = {
  version: () => req<VersionResp>('/api/system/version'),
  stats: () => req<{ goroutines: number }>('/api/system/stats'),
  selfUpdate: (target: string) =>
    req<{ ok?: boolean; armed?: boolean; note?: string }>('/api/system/self-update', {
      method: 'POST', body: JSON.stringify({ target_version: target }),
    }),
  selfUpdateTags: (refresh = false) =>
    req<SelfUpdateTag[]>(`/api/system/self-update/tags${refresh ? '?refresh=1' : ''}`),
  selfUpdateStatus: () => req<SelfUpdateRunStatus>('/api/system/self-update/status'),
}

// -------- maintenance --------
export const docker = {
  prune: () => req<{ bytes_freed: number; output: string }>('/api/docker/prune', { method: 'POST' }),
}

// -------- sysconfig --------
export type SecretValue = { has_value: boolean }
export type ConfigField = {
  key: string
  label: string
  doc: string
  used_by: string[]
  source: string
  editable: boolean
  secret: boolean
  value: unknown
}
export type ConfigSection = {
  id: string
  title: string
  intro: string
  fields: ConfigField[]
  operations?: string[]
  error?: string
}

export const sysconfig = {
  list: () => req<{ sections: ConfigSection[] }>('/api/sysconfig'),
}

// -------- streams --------
export const streams = {
  events: () => '/api/events',
  serviceLogs: (id: number, tail = 200) => `/api/services/${id}/logs?tail=${tail}`,
  deployLog: (id: number) => `/api/deployments/${id}/log`,
}
